import React, { useEffect, createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { getCurrentUser } from '../store/slices/authSlice';

// Create a context for authentication
interface AuthContextType {
  lastChecked: number;
  refreshAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  lastChecked: 0,
  refreshAuth: async () => false
});

/**
 * Hook to use the auth context
 */
export const useAuthContext = () => useContext(AuthContext);

/**
 * AuthProvider component
 * 
 * Wraps the application and ensures regular authentication checks
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [lastChecked, setLastChecked] = useState<number>(0);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [authCheckInterval, setAuthCheckInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Refresh auth function
  const refreshAuth = async (): Promise<boolean> => {
    try {
      // Only attempt to refresh auth if a token exists and haven't exceeded retry count
      const token = localStorage.getItem('authToken');
      if (!token || retryCount >= 3) {
        if (retryCount >= 3) {
          console.error('Authentication failed after 3 attempts');
          localStorage.removeItem('authToken');
        }
        return false;
      }
      
      await dispatch(getCurrentUser());
      setLastChecked(Date.now());
      // Reset retry count on success
      setRetryCount(0);
      return true;
    } catch (error) {
      console.error('Authentication refresh failed:', error);
      // Increment retry count
      setRetryCount(prev => prev + 1);
      return false;
    }
  };
  
  // Initial auth check
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      refreshAuth();
    }
    
    // Cleanup function
    return () => {
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
      }
    };
  }, []);
  
  // Set up regular auth checks if authenticated
  useEffect(() => {
    // Clear existing interval
    if (authCheckInterval) {
      clearInterval(authCheckInterval);
      setAuthCheckInterval(null);
    }
    
    if (!isAuthenticated) return;
    
    // Check auth every 5 minutes if authenticated
    const interval = setInterval(() => {
      // Only refresh if last check was more than 4 minutes ago
      if (Date.now() - lastChecked > 4 * 60 * 1000) {
        refreshAuth();
      }
    }, 5 * 60 * 1000);
    
    setAuthCheckInterval(interval);
    
    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated, lastChecked]);
  
  // Create context value
  const contextValue: AuthContextType = {
    lastChecked,
    refreshAuth
  };
  
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 